import Data from 'data';

import { itemAllowed } from "./item";
import { cubeItemAllowed } from "./cube";
import { itemFix, itemImportUnlock, itemSetAncient, itemSetAugment, itemSetId, itemSetStatsPercent } from './itemReducers';
import { compositeStats, skillCompositeId } from './optimizer';
import { Seasons } from 'common';

import { ProfileStore } from 'store';

export function profileNewId() {
  return ProfileStore.newId();
}

function profileUpdatePriority(profile) {
  const composite = new Set(Object.keys(compositeStats));
  for (const skill of profile.skills) {
    if (skill) composite.add(skillCompositeId(profile.class, skill[0]));
  }

  const valid = prio => !prio.stat || compositeStats[prio.stat] || Data.stats[prio.stat];

  if (profile.statPriority.some(prio => !valid(prio) || !prio.id)) {
    let maxId = profile.statPriority.reduce((prev, prio) => Math.max(prev, prio.id || 0), 0);
    profile.statPriority = profile.statPriority.map(prio => {
      if (!valid(prio)) {
        return {stat: null, options: {}, id: prio.id || ++maxId};
      } else if (!prio.id) {
        return {...prio, id: ++maxId};
      } else {
        return prio;
      }
    });
  }
}

export function profileSetClass(profile, charClass) {
  if (charClass === profile.class) return profile;

  const result = {...profile, class: charClass};
  result.items = {};
  for (const slot in profile.items) {
    if (profile.items[slot] && itemAllowed(result, slot, profile.items[slot].id)) {
      result.items[slot] = profile.items[slot];
    }
  }
  result.skills = [...Array(6)];
  result.passives = [...Array(4)];

  result.kanai = {};
  for (const slot in profile.kanai) {
    if (cubeItemAllowed(result, slot, profile.kanai[slot])) {
      result.kanai[slot] = profile.kanai[slot];
    }
  }

  profileUpdatePriority(result);

  return result;
}

export function profileSetFollower(profile, follower) {
  if (follower === profile.follower) return profile;

  const result = {...profile, follower};
  result.followerItems = {};
  // don't unequip follower items if no follower is selected - so users don't lose work
  for (const slot in profile.followerItems) {
    if (profile.followerItems[slot] && itemAllowed(result, slot, profile.followerItems[slot].id, true)) {
      result.followerItems[slot] = profile.followerItems[slot];
    }
  }
  result.followerSkills = [...Array(4)];

  return result;
}

export function profileSetSeasonal(profile, seasonal) {
  if (seasonal === profile.seasonal) return profile;

  const result = {...profile, seasonal};

  result.items = {};
  for (const slot in profile.items) {
    if (profile.items[slot] && itemAllowed(result, slot, profile.items[slot].id)) {
      result.items[slot] = profile.items[slot];
      if (seasonal !== Seasons.SoulShards && result.items[slot].gems?.[0]?.id) {
        result.items[slot] = {...result.items[slot]};
        result.items[slot].gems = result.items[slot].gems.slice();
        result.items[slot].gems[0] = null;
      }
    }
  }

  result.kanai = {};
  for (const slot in profile.kanai) {
    if (cubeItemAllowed(result, slot, profile.kanai[slot])) {
      result.kanai[slot] = profile.kanai[slot];
    }
  }

  return result;
}

function getEquipSetItems(profile, set, follower) {
  const info = Data.itemSets[set];
  const list = {};
  const equipped = {};
  const items = (follower ? profile.followerItems : profile.items);
  for (const slot in items) {
    if (items[slot]) {
      equipped[items[slot].id] = slot;
    }
  }
  for (const item of info.items) {
    if (equipped[item.id]) continue;
    const type = item.type;
    const slot = Data.itemTypes[type].slot;
    const slots = (Data.metaSlots[slot] && Data.metaSlots[slot].slots) || [slot];
    const matchSlot = slots.find(slot => !list[slot] && !items[slot] && itemAllowed(profile, slot, item.id, follower));
    if (matchSlot) {
      list[matchSlot] = item.id;
    }
  }
  return list;
}

export function profileCanEquipSet(profile, set, follower) {
  const items = getEquipSetItems(profile, set, follower);
  return Object.keys(items).length > 0;
}

export function profileEquipSet(profile, set, follower) {
  const items = getEquipSetItems(profile, set);
  profile = {...profile};
  if (follower) {
    profile.followerItems = {...profile.followerItems};
    for (const slot in items) {
      profile.followerItems[slot] = itemSetId(null, items[slot], profile.follower);
    }
  } else {
    profile.items = {...profile.items};
    for (const slot in items) {
      profile.items[slot] = itemSetId(null, items[slot], profile.class);
    }
  }
  return profile;
}

export function profileHasImports(profile, follower) {
  const items = (follower ? profile.followerItems : profile.items);
  for (const slot in items) {
    if (items[slot]?.imported) {
      return true;
    }
  }
  return false;
}

export function profileHasAncients(profile, follower) {
  const items = (follower ? profile.followerItems : profile.items);
  for (const slot in items) {
    if (items[slot]?.ancient) {
      return true;
    }
  }
  return false;
}

export function profileImportUnlock(profile, follower) {
  const items = (follower ? profile.followerItems : profile.items);
  const result = {};
  for (const slot in profile.items) {
    result[slot] = itemImportUnlock(items[slot]);
  }
  profile = {...profile};
  if (follower) {
    profile.followerItems = result;
  } else {
    profile.items = result;
  }
  return profile;
}

export function profileSetAncient(profile, ancient, follower) {
  const items = (follower ? profile.followerItems : profile.items);
  const result = {};
  for (const slot in items) {
    result[slot] = itemSetAncient(items[slot], ancient, profile.class);
  }
  profile = {...profile};
  if (follower) {
    profile.followerItems = result;
  } else {
    profile.items = result;
  }
  return profile;
}

export function profileSetStatsPercent(profile, percent, unlock, follower) {
  const items = (follower ? profile.followerItems : profile.items);
  const result = {};
  for (const slot in items) {
    result[slot] = itemSetStatsPercent(items[slot], percent, unlock);
  }
  profile = {...profile};
  if (follower) {
    profile.followerItems = result;
  } else {
    profile.items = result;
  }
  return profile;
}

export function profileSetAugment(profile, stat, level, follower) {
  const items = (follower ? profile.followerItems : profile.items);
  const result = {};
  for (const slot in items) {
    result[slot] = itemSetAugment(items[slot], stat, level);
  }
  profile = {...profile};
  if (follower) {
    profile.followerItems = result;
  } else {
    profile.items = result;
  }
  return profile;
}

export function profileSetSkill(profile, index, skill) {
  if (!skill && !profile.skills[index]) return profile;
  if (skill && profile.skills[index] && profile.skills[index][0] === skill[0] && profile.skills[index][1] === skill[1]) return profile;

  profile = {...profile};
  profile.skills = profile.skills.slice();
  profile.skills[index] = skill;

  profileUpdatePriority(profile);
  return profile;
}

export function profileFix(profile) {
  profile = {...ProfileStore.initialState, ...profile};
  profile.id = profileNewId();
  profile.items = {...profile.items};
  if (!Object.values(Seasons).includes(profile.seasonal)) {
    profile.seasonal = Seasons.None;
  }
  for (const slot in profile.items) {
    profile.items[slot] = itemFix(profile.items[slot]);
  }
  profileUpdatePriority(profile);

  profile.followerItems = {...profile.followerItems};
  for (const slot in profile.followerItems) {
    profile.followerItems[slot] = itemFix(profile.followerItems[slot]);
  }

  return profile;
}

export function profileIsEmpty(profile) {
  if (profile.skills.some(Boolean)) return false;
  if (profile.passives.some(Boolean)) return false;
  if (profile.paragon.level) return false;
  if (profile.follower) return false;
  if (Object.values(profile.items).some(Boolean)) return false;
  if (Object.values(profile.kanai).some(Boolean)) return false;
  return true;
}
